import { createContext } from 'preact';
import { useContext, useRef, useEffect, useReducer } from 'preact/hooks';
import { tryFocus, rnd, addClass, removeClass, setCookie, isLandingPage } from '@/utils';
import { usePrevious, useDetectInteraction } from '@/hooks';
import {
	showNextButtonWhenAutoPost,
	noInputBlocks,
	noTriggerNextBlocks
} from '@/constants';
import { getCurrentVisibleId } from '../utils';
import useFeedbackId from '../hooks/useFeedbackId';
import useCustomerId from '../hooks/useCustomerId';
import useBlock from '../hooks/useBlock';
import useFormTypePositionButton from '../hooks/useFormTypePositionButton';
import useFormWidth from '../hooks/useFormWidth';
import useWrapStyle from '../hooks/useWrapStyle';
import usePages from '../hooks/usePages';
import useLastVisibleQuestionBlock from '../hooks/useLastVisibleQuestionBlock';
import useValidatedBlocks from '../hooks/useValidatedBlocks';
import useSyncExternal from '../hooks/useSyncExternal';
import useHandleExtraProps from '../hooks/useHandleExtraProps';
import useIntegrations from '../hooks/useIntegrations';
import useJsEvents from '../hooks/useJsEvents';
import useResetForm from '../hooks/useResetForm';
import useLastConversationalBlockIsVisible from '../hooks/useLastConversationalBlockIsVisible';
import usePostFeedback from '../api/usePostFeedback';
import usePostNotifyUser from '../api/usePostNotifyUser';
import { useFormProps, useConfigStore } from './ConfigStore';
import { useBlockStore } from './BlockStore';
import useNextVisiblePageNumber from '../hooks/useNextVisiblePageNumber';
import usePrevVisiblePageNumber from '../hooks/usePrevVisiblePageNumber';

const FormStoreContext = createContext({});

function reducer(state, action) {
	switch (action.type) {
		case 'toggle_open':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				formOpen: typeof action.payload !== 'undefined' ? action.payload : !state.formOpen
			};

		case 'start_form':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				formIsOnOpenerPage: false
			};

		case 'set_completed':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				formIsCompleted: true,
				loading: true
			};

		case 'set_loading_done':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				loading: false
			};

		case 'reset':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				...action.payload
			};

		case 'toggle_screenshot':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				screenshotActive: state.payload ?? !state.screenshotActive
			};

		case 'update_screenreader_helper':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				screenReaderHelperText: action.payload ?? ''
			};

		case 'update_conversational_focus_state_helper':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				conversationalFocusStateValue: action.payload ?? ''
			};

		case 'update_last_key_pressed':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				lastKeyPressed: action.payload ?? null
			};

		case 'set_show_errors':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				showErrors: true
			};

		case 'increment_page':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				showErrors: false,
				activePage: action.payload?.to ?? state.activePage + 1,
				direction: 'next'
			};

		case 'decrement_page':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				showErrors: false,
				activePage: action.payload?.to ?? state.activePage - 1,
				direction: 'prev'
			};

		case 'to_page':
			return {
				...state,
				showErrors: false,
				activePage: Number(action.payload),
				direction: 'next'
			};

		case 'handle_show_nav':
			return {
				...state,
				showNavAutopostPages:
					state.showNavAutopostPages.indexOf(state.activePage) === -1
						? [...state.showNavAutopostPages, state.activePage]
						: state.showNavAutopostPages
			};

		case 'handle_autoskip':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				...(action.payload.pageValid &&
					!action.payload.isLastPage &&
					!action.payload.extraInputElementIsVisible &&
					!action.payload.lastVisibleElementIsShowButtonType && {
						activePage: action.payload.nextPageWithVisibleBlocks
					}),
				...(!action.payload.pageValid && {
					showErrors: true
				}),
				...(action.payload.pageValid &&
					action.payload.isLastPage &&
					!action.payload.extraInputElementIsVisible &&
					!action.payload.lastVisibleElementIsShowButtonType && {
						formIsCompleted: true,
						loading: true
					})
			};

		case 'set_hide_landingspage_form':
			return {
				...state,
				action: `${action.type}-${rnd()}`,
				hideLandingPageForm: true
			};

		default:
			return state;
	}
}

function useCreateFormStore() {
	const { isCompleted, webView } = useFormProps();
	const { properties, pageMap, text } = useConfigStore();

	const { formType, breakpoint, position, isPastBreakpoint, buttonType } =
		useFormTypePositionButton();

	const initialVisiblePage = useNextVisiblePageNumber(0);

	const [state, dispatch] = useReducer(reducer, {
		action: '',
		activePage: initialVisiblePage,
		formIsOnOpenerPage: Boolean(properties?.introPage),
		formIsCompleted: isCompleted,
		formOpen: formType === 'embed',
		screenshotActive: false,
		hideLandingPageForm: false,
		direction: 'next',
		showNavAutopostPages: [],
		screenReaderHelperText: null,
		conversationalFocusStateValue: '',
		lastKeyPressed: null
	});

	const feedbackId = useFeedbackId(state.formIsCompleted);
	const customerId = useCustomerId(state.formIsCompleted);

	useHandleExtraProps(dispatch);

	const contentDivRef = useRef(null);
	const feedbackButtonRef = useRef(null);
	const formBodyRef = useRef(null);
	const closeButtonRef = useRef(null);
	const brandingRef = useRef(null);
	const actionButtonRef = useRef(null);
	const shiftFocusToTopRef = useRef(null);
	const screenshotButtonRef = useRef(null);
	const firstFocusableBlockRef = useRef(null);
	const conversationalDummyFocusBlockRef = useRef(null);
	const currentConversationalFocusRef = useRef(null);

	const { formWidth, widthClass, clientWidth } = useFormWidth({
		formType,
		contentDivRef,
		webView
	});

	const { wrapStyle, formOffset } = useWrapStyle({
		feedbackButtonRef,
		formWidth: clientWidth,
		formOpen: state.formOpen
	});

	useSyncExternal({
		...state,
		formType
	});

	const nextVisiblePage = useNextVisiblePageNumber(state.activePage);
	const prevVisiblePage = usePrevVisiblePageNumber(state.activePage);

	return {
		...state,
		feedbackId,
		customerId,
		formType,
		breakpoint,
		position,
		isPastBreakpoint,
		buttonType,
		formWidth,
		widthClass,
		wrapStyle,
		formOffset,
		contentDivRef,
		feedbackButtonRef,
		formBodyRef,
		closeButtonRef,
		shiftFocusToTopRef,
		firstFocusableBlockRef,
		brandingRef,
		actionButtonRef,
		screenshotButtonRef,
		initialVisiblePage,
		conversationalDummyFocusBlockRef,
		currentConversationalFocusRef,
		//postFeedbackError: error,
		actions: {
			dispatch,
			toggleForm: () => {
				dispatch({
					type: 'toggle_open'
				});
			},
			toggleScreenshot: payload => {
				dispatch({
					type: 'toggle_screenshot',
					payload
				});
			},
			updateScreenReaderHelper: payload => {
				dispatch({
					type: 'update_screenreader_helper',
					payload
				});
			},
			updateLastKeyPressed: payload => {
				dispatch({
					type: 'update_last_key_pressed',
					payload
				});
			},
			startForm: () => {
				dispatch({
					type: 'start_form'
				});
			},
			nextPage: () => {
				const lastPage = Number(Object.keys(pageMap).pop());
				if (state.activePage !== lastPage && nextVisiblePage) {
					dispatch({
						type: 'increment_page',
						payload: {
							to: nextVisiblePage
						}
					});
				}

				if (!nextVisiblePage) {
					dispatch({
						type: 'set_completed'
					});
				}
				if (nextVisiblePage) {
					setTimeout(() => {
						tryFocus(
							firstFocusableBlockRef?.current?.querySelector(
								'input, textarea, .ui-stars-star, .nice-select, a, .section-screenshot, .btn.btn-next, .btn.btn-submit'
							)
						);
					}, 100);
				}
			},
			prevPage: () => {
				if (prevVisiblePage) {
					dispatch({
						type: 'decrement_page',
						payload: {
							to: prevVisiblePage
						}
					});

					tryFocus(
						firstFocusableBlockRef?.current?.querySelector(
							'input, textarea, .ui-stars-star, .nice-select, a, .section-screenshot, .btn.btn-next, .btn.btn-submit'
						),
						100
					);
				}
			},
			reset: () => {
				dispatch({
					type: 'reset',
					payload: {
						activePage: 1,
						formIsOnOpenerPage: Boolean(properties?.introPage),
						formIsCompleted: false,
						showErrors: false,
						direction: 'next',
						showNavAutopostPages: []
					}
				});
			},
			postFeedback: () => {
				dispatch({
					type: 'set_completed'
				});
			},
			handleWCAGkeyEvents: e => {
				if (e.key === 'Escape') {
					dispatch({
						type: 'toggle_open'
					});
				}
				if (
					//WCAG required - prevent key combinations in background
					e.shiftKey &&
					['Enter', 'Space'].indexOf(e.code) > -1
				) {
					e.stopPropagation();
				}
			},
			handleConversationalFocus: setFocusToDummyElement => {
				/*  When answering a conversational question, the question component dismounts on answer.
					To prevent losing the focus, we use a hidden dummy element to keep the focus temporarily until the next question is loaded. (2 steps)
					1. focus on 'mop-conversational-focus-dummy'. ( hidden below inteactive element.)
					2. when new question is rendered, focus on the new interactive element.
				*/
				dispatch({
					type: 'update_conversational_focus_state_helper',
					payload: setFocusToDummyElement
						? (text?.wcag?.chatbotTypingHelperText ?? 'The chatbot is typing')
						: ''
				});
				setTimeout(
					() => {
						dispatch({
							type: 'update_last_key_pressed',
							payload: null
						});
					},
					setFocusToDummyElement ? 500 : 0
				);

				if (setFocusToDummyElement) {
					tryFocus(conversationalDummyFocusBlockRef.current);
				} else {
					tryFocus(
						currentConversationalFocusRef?.current?.querySelector(
							'button, a, input, textarea'
						),
						100
					);
				}
			}
		}
	};
}

function useRespondToFormStoreAction(actionType, fn) {
	const { action } = useFormStore();

	useRespondToAction(action, actionType, fn);
}

function useRespondToBlockStoreAction(actionType, fn) {
	const { action } = useBlockStore();

	useRespondToAction(action, actionType, fn);
}

function useRespondToFormPropsAction(actionType, fn) {
	const { openAction } = useFormProps();
	useRespondToAction(openAction, actionType, fn);
}

function useRespondToAction(action, actionType, fn) {
	const prevAction = usePrevious(action);

	const [type, instance] = action?.split('-') ?? '';
	const [prevType, prevInstance] = prevAction?.split('-') ?? '';

	useEffect(() => {
		if (!type || type !== actionType) {
			return;
		}
		if (instance !== prevInstance) {
			fn();
		}
	}, [type, instance, prevType, prevInstance, actionType, fn]);
}

function FormActionResponder() {
	const { formOpen, cookieTime, formKey, webView } = useFormProps();
	const resetForm = useResetForm();
	const lastConversationalBlockVisible = useLastConversationalBlockIsVisible();

	useRespondToFormPropsAction('form_open', () => {
		if (formOpen !== null) {
			setTimeout(() => {
				dispatch({
					type: 'toggle_open',
					payload: formOpen
				});
			});
		}
	});

	const { properties, pageMap, blocks, layout, blockRules } = useConfigStore();
	const blockStore = useBlockStore();
	const { request } = usePostFeedback();
	const notifyUser = usePostNotifyUser();
	const integrations = useIntegrations();
	const triggerEvent = useJsEvents();
	const {
		actions: { dispatch },
		...state
	} = useFormStore();

	const pageLayout = usePages(state.activePage);
	const nextPageWithVisibleBlocks = useNextVisiblePageNumber(state.activePage);

	const { pageValid } = useValidatedBlocks(pageLayout);
	const { block: lastVisibleBlock, state: lastVisibleState } =
		useLastVisibleQuestionBlock(pageLayout);
	const currentPageBreak = useBlock({
		typeName: 'page_break',
		typeOf: state.activePage - 1
	});

	const lastUpdated = useBlock({ id: blockStore.lastUpdatedBlock });
	const interactionDetected = useDetectInteraction();

	const isLastPage =
		Number(Object.keys(pageMap).pop()) === state.activePage ||
		nextPageWithVisibleBlocks === null;
	const extraInputElement =
		lastVisibleBlock?.properties?.elements_extra?.[
			Object.keys(lastVisibleBlock?.properties?.elements_extra ?? {}).pop()
		];
	const extraInputElementIsVisible =
		lastVisibleState?.[extraInputElement?.data_field]?.isVisible;

	const lastVisibleElementIsShowButtonType =
		showNextButtonWhenAutoPost.indexOf(lastVisibleBlock?.typeName) > -1;

	const doAutopost =
		['textarea', 'input', 'contact', 'checkbox'].indexOf(lastVisibleBlock?.typeName) ===
			-1 &&
		state.showNavAutopostPages.indexOf(state.activePage) === -1 &&
		blockStore.lastUpdatedBlock &&
		blockStore.lastUpdatedBlock === lastVisibleBlock?.id &&
		!blockStore.submitFromLogic &&
		((isLastPage && properties.advanced.lastPageAutoPost) ||
			currentPageBreak?.block?.properties?.autopost);

	const htmlBodyClass =
		state.formType === 'slide' ? 'mopinion-slider-active' : 'mopinion-modal-active';
	const html = document.querySelector('html');
	const body = document.body;

	useEffect(() => {
		triggerEvent('loaded');
	}, []);

	useEffect(() => {
		if (state.formType === 'embed') {
			triggerEvent('shown');
			integrations.sendEvent(`Form shown`);
		}
	}, []);

	useEffect(() => {
		if (state.initialVisiblePage !== 1 && interactionDetected) {
			request();
		}
	}, [state.initialVisiblePage, interactionDetected]);

	useRespondToFormStoreAction('toggle_open', () => {
		integrations.sendEvent(`Form ${state.formOpen ? 'shown' : 'hidden'}`);

		triggerEvent(state.formOpen ? 'shown' : 'hidden');

		if (state.formOpen && state.formType !== 'embed') {
			tryFocus(state.contentDivRef.current, 500);
			addClass([html, body], htmlBodyClass);
		} else {
			removeClass([html, body], htmlBodyClass);
		}

		if (state.formOpen) {
			setCookie('MSopened', formKey, cookieTime);
			setCookie(`MSopened.${formKey}`, true, cookieTime);
		}

		if (!state.formOpen && !properties.conversational && state.formIsCompleted) {
			setTimeout(() => resetForm(), 200);
		}
	});

	useRespondToFormStoreAction('toggle_screenshot', () => {
		if (state.formType === 'embed') {
			return;
		}

		if (state.screenshotActive) {
			removeClass([html, body], htmlBodyClass);
		} else {
			addClass([html, body], htmlBodyClass);

			tryFocus(state.screenshotButtonRef.current);
		}
	});

	const scrollIntoView = () => {
		try {
			if (state.formType === 'embed' && !isLandingPage()) {
				return;
			}

			state.contentDivRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start'
			});
		} catch (e) {}
	};

	useRespondToFormStoreAction('increment_page', () => {
		request();
		triggerEvent('next');
		integrations.sendEvent('Next page');
		scrollIntoView();
	});

	useRespondToFormStoreAction('decrement_page', () => {
		request();
		triggerEvent('previous');
		integrations.sendEvent('Previous page');
	});

	const handleSetCompleted = () => {
		triggerEvent('feedback_sent');
		integrations.sendEvent('Feedback sent');
		integrations.sendFeedback();

		setCookie('MSFeedbackSent', formKey, cookieTime);
		setCookie(`MSFeedbackSent.${formKey}`, true, cookieTime);

		if (properties.notify?.send) {
			notifyUser.request();
		}

		if (properties.timeout) {
			setTimeout(() => {
				dispatch({
					type: 'toggle_open',
					payload: false
				});
			}, [Number(properties.timeout)]);
		}
	};

	useRespondToFormStoreAction('handle_autoskip', () => {
		if (pageValid) {
			request({
				...(isLastPage && {
					callback: () => {
						dispatch({
							type: 'set_loading_done'
						});
						if (state.formIsCompleted) {
							handleSetCompleted();
						}
					}
				})
			});
		}
	});

	useRespondToFormStoreAction('set_completed', () => {
		request({
			callback: () => {
				dispatch({
					type: 'set_loading_done'
				});
				handleSetCompleted();
			}
		});
	});

	useRespondToFormStoreAction('set_loading_done', () => {
		scrollIntoView();
	});

	//handle conversational
	const handleShowNextConvo = () => {
		setTimeout(() => {
			if (lastConversationalBlockVisible) {
				dispatch({
					type: 'set_completed'
				});
				return;
			}

			const currentId = getCurrentVisibleId({
				layout,
				blockState: blockStore,
				blocks
			});

			const currentBlock = blocks[currentId] ?? {};
			const currentState = blockStore[currentId] ?? {};
			//if no current visible element is found = start of conversation
			//if current visible element is website_data the first element is website data block
			//if current visible element is a no input block, show next visible element

			if (
				!currentBlock?.typeName ||
				(noInputBlocks.indexOf(currentBlock.typeName) > -1 &&
					noTriggerNextBlocks.indexOf(currentBlock.typeName) === -1) ||
				(!currentState.showError && currentState.value && !currentState.wasPrefilled) ||
				currentState.isSkipped
			) {
				blockStore.actions.dispatch({
					type: 'set_show_next_convo',
					payload: {
						blocks,
						layout,
						blockRules
					}
				});
			}
		});
	};

	useEffect(() => {
		if (properties.conversational) {
			handleShowNextConvo();
		}
	}, [properties.conversational]);

	useRespondToBlockStoreAction('reset_form', () => {
		if (properties.conversational) {
			handleShowNextConvo();
		}
	});
	useRespondToBlockStoreAction('set_show_next_convo', () => {
		handleShowNextConvo();
	});

	useRespondToBlockStoreAction('set_skip', () => {
		handleShowNextConvo();
	});

	useRespondToBlockStoreAction('set_value', () => {
		if (properties.conversational) {
			request();
			handleShowNextConvo();
			triggerEvent('question_answered');
			integrations.sendEvent('Question answered');
		}
	});
	useRespondToBlockStoreAction('set_nested_value', () => {
		if (
			properties.conversational &&
			lastUpdated.block?.id &&
			lastUpdated.block?.typeName !== 'website_data'
		) {
			request();
			handleShowNextConvo();
			triggerEvent('question_answered');
			integrations.sendEvent('Question answered');
		}
	});

	//handle submit logic
	useEffect(() => {
		if (blockStore.initialPost && interactionDetected) {
			request();
			if (pageValid && (doAutopost || blockStore.submitFromLogic)) {
				dispatch({
					type: 'handle_autoskip',
					payload: {
						isLastPage,
						pageValid,
						extraInputElementIsVisible,
						lastVisibleElementIsShowButtonType,
						nextPageWithVisibleBlocks
					}
				});
			}
		}
	}, [interactionDetected]);

	useEffect(() => {
		if (blockStore.submitFromLogic) {
			dispatch({
				type: 'set_completed'
			});
		}
	}, [blockStore.submitFromLogic]);

	//handle autopost when last element is answered - todo -> move to separate hook
	useRespondToBlockStoreAction('set_value', () => {
		if (
			blockStore.lastUpdatedBlock === lastVisibleBlock?.id ||
			lastVisibleElementIsShowButtonType
		) {
			dispatch({
				type: 'handle_show_nav'
			});
		}
	});

	useRespondToBlockStoreAction('set_nested_value', () => {
		if (
			blockStore.lastUpdatedBlock === lastVisibleBlock?.id ||
			lastVisibleElementIsShowButtonType
		) {
			dispatch({
				type: 'handle_show_nav'
			});
		}
	});

	useRespondToBlockStoreAction('set_value', () => {
		if (doAutopost) {
			request();
			if (pageValid) {
				dispatch({
					type: 'handle_autoskip',
					payload: {
						isLastPage,
						pageValid,
						extraInputElementIsVisible,
						lastVisibleElementIsShowButtonType,
						nextPageWithVisibleBlocks
					}
				});
			}
		}
	});
	useRespondToBlockStoreAction('set_nested_value', () => {
		if (doAutopost) {
			request();
			if (pageValid) {
				dispatch({
					type: 'handle_autoskip',
					payload: {
						isLastPage,
						pageValid,
						extraInputElementIsVisible,
						lastVisibleElementIsShowButtonType,
						nextPageWithVisibleBlocks
					}
				});
			}
		}
	});

	return null;
}

export function useFormStore() {
	return useContext(FormStoreContext);
}

export function FormStoreProvider({ children }) {
	const store = useCreateFormStore();
	return (
		<FormStoreContext.Provider value={store}>
			<FormActionResponder />
			{children}
		</FormStoreContext.Provider>
	);
}
